import { authenticatedFetch } from './authenticatedFetch';
import { formatApiErrorMessage } from '../utils/apiErrorMessage';
import { fetchAllEvaluatedKeyPages } from '../utils/paginatedFetch';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const TEMPLATES_BASE_PATH =
  import.meta.env.VITE_TEMPLATES_BASE_PATH ||
  import.meta.env.VITE_USER_TEMPLATES_LIST_PATH ||
  '/templates';

function buildTemplatesUrl(id, suffix = '') {
  const base = String(TEMPLATES_BASE_PATH).replace(/\/+$/, '');
  if (id == null || id === '') {
    return `${API_BASE_URL}${base}${suffix}`;
  }
  const encodedId = encodeURIComponent(String(id));
  return `${API_BASE_URL}${base}/${encodedId}${suffix}`;
}

function normalizeTemplateItem(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const id = raw.id ?? raw.template_id ?? raw.templateId;
  if (id == null || id === '') return null;

  return {
    id: String(id),
    template_id: String(id),
    name: String(raw.name ?? raw.title ?? 'Sem nome').trim() || 'Sem nome',
    templateKey: raw.templateKey ?? raw.template_key ?? null,
    description: raw.description ?? raw.desc ?? '',
    created_at: raw.created_at ?? raw.createdAt ?? null,
    s3_key: raw.s3_key ?? raw.s3Key ?? null,
    docx_status: raw.docx_status ?? raw.docxStatus ?? null,
    user_id: raw.user_id ?? raw.userId ?? null,
  };
}

function normalizeTemplatesList(data) {
  if (Array.isArray(data)) {
    return data.map(normalizeTemplateItem).filter(Boolean);
  }

  if (data && typeof data === 'object') {
    const list =
      data.items ??
      data.templates ??
      data.data ??
      data.results ??
      null;

    if (Array.isArray(list)) {
      return list.map(normalizeTemplateItem).filter(Boolean);
    }
  }

  return [];
}

function normalizeTemplateDetail(data) {
  const raw = data?.template ?? data?.item ?? data;
  const base = normalizeTemplateItem(raw);
  if (!base) return null;

  return {
    ...base,
    fields: Array.isArray(raw?.fields) ? raw.fields : [],
    content: raw?.content ?? raw?.editorDocument ?? null,
  };
}

/**
 * GET /templates — single page.
 */
export async function listUserTemplatesPage(lastEvaluatedKey = null) {
  try {
    const params = new URLSearchParams();
    if (lastEvaluatedKey) {
      params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
    }

    const response = await authenticatedFetch(
      `${buildTemplatesUrl()}?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao carregar templates.'),
      };
    }

    const data = await response.json();
    const items = normalizeTemplatesList(data);
    const lastKey =
      data?.last_evaluated_key ??
      data?.lastEvaluatedKey ??
      null;

    return {
      success: true,
      data: {
        items,
        lastEvaluatedKey: lastKey,
        count: data?.count ?? items.length,
      },
    };
  } catch (error) {
    console.error('listUserTemplatesPage', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao carregar templates.'),
    };
  }
}

/**
 * GET /templates
 *
 * @returns {Promise<{ success: true; data: { items: object[] } } | { success: false; error: string }>}
 */
export async function listUserTemplates() {
  return fetchAllEvaluatedKeyPages((lastEvaluatedKey) =>
    listUserTemplatesPage(lastEvaluatedKey)
  );
}

/**
 * GET /templates/{id}
 *
 * @param {string} id
 */
export async function getUserTemplateById(id) {
  const templateId = String(id ?? '').trim();
  if (!templateId) {
    return { success: false, error: 'ID do template inválido.' };
  }

  try {
    const response = await authenticatedFetch(buildTemplatesUrl(templateId), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao carregar template.'),
      };
    }

    const data = await response.json();
    const template = normalizeTemplateDetail(data);

    if (!template) {
      return { success: false, error: 'Resposta inválida do servidor.' };
    }

    return { success: true, data: template };
  } catch (error) {
    console.error('getUserTemplateById', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao carregar template.'),
    };
  }
}

/**
 * POST /templates
 *
 * @param {{
 *   id: string;
 *   name: string;
 *   fields: Array<{ key: string; name: string; type: string; required: boolean }>;
 *   content: { type: 'doc'; content: unknown[] };
 * }} payload
 */
export async function saveUserTemplate(payload) {
  try {
    const response = await authenticatedFetch(buildTemplatesUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao salvar template.'),
      };
    }

    let data = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await response.json();
    }

    return { success: true, data };
  } catch (error) {
    console.error('saveUserTemplate', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao salvar template.'),
    };
  }
}

/**
 * DELETE /templates/{id}
 *
 * @param {string} id
 */
export async function deleteUserTemplate(id) {
  const templateId = String(id ?? '').trim();
  if (!templateId) {
    return { success: false, error: 'ID do template inválido.' };
  }

  try {
    const response = await authenticatedFetch(buildTemplatesUrl(templateId), {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao excluir template.'),
      };
    }

    return { success: true };
  } catch (error) {
    console.error('deleteUserTemplate', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao excluir template.'),
    };
  }
}

/**
 * POST /templates/{id}/duplicate
 *
 * @param {string} id
 * @returns {Promise<{ success: true; data: object } | { success: false; error: string }>}
 */
export async function duplicateUserTemplate(id) {
  const templateId = String(id ?? '').trim();
  if (!templateId) {
    return { success: false, error: 'ID do template inválido.' };
  }

  try {
    const response = await authenticatedFetch(buildTemplatesUrl(templateId, '/duplicate'), {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao duplicar template.'),
      };
    }

    const data = await response.json();
    const template = normalizeTemplateItem(data?.template ?? data);

    if (!template) {
      return { success: false, error: 'Resposta inválida do servidor.' };
    }

    return { success: true, data: template };
  } catch (error) {
    console.error('duplicateUserTemplate', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao duplicar template.'),
    };
  }
}

function buildDocxDownloadFileName(name, templateKey) {
  const base = String(name ?? templateKey ?? 'template').trim() || 'template';
  return `${base}.docx`;
}

/**
 * POST /templates/{template_key}/download
 *
 * @param {string} templateKey
 * @param {string} [name] — used for the downloaded filename
 */
export async function downloadTemplateDocx(templateKey, name) {
  const key = String(templateKey ?? '').trim();
  if (!key) {
    return { success: false, error: 'Template key inválido.' };
  }

  try {
    const response = await authenticatedFetch(buildTemplatesUrl(key, '/download'), {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao baixar template.'),
      };
    }

    const blob = await response.blob();

    return {
      success: true,
      data: {
        blob,
        fileName: buildDocxDownloadFileName(name, key),
      },
    };
  } catch (error) {
    console.error('downloadTemplateDocx', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao baixar template.'),
    };
  }
}
